/**
 * Class definition for `wp-accordion-heading`
 *
 * @class WPAccordionHeading
 * @extends {HTMLElement}
 */
class WPAccordionHeading extends HTMLElement {
  constructor(self) {
    self = super(self);
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', -1);
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-accordion-heading';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['selected', 'expanded'];
  }

  /**
   * Getter for `selected` attribute
   */
  get selected() {
    return this.hasAttribute('selected');
  }

  /**
   * Setter for `selected` attribute
   */
  set selected(value) {
    if (Boolean(value)) {
      this.setAttribute('selected', '');
    } else {
      this.removeAttribute('selected');
    }
  }

  /**
   * Getter for `expanded` attribute
   */
  get expanded() {
    return this.hasAttribute('expanded');
  }

  /**
   * Setter for `expanded` attribute
   */
  set expanded(value) {
    if (Boolean(value)) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    this.addEventListener('click', this._handleClick, false);
    this.addEventListener('focus', this._handleFocus, false);
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'expanded':
        this._expandedChanged();
      case 'selected':
        this._selectedChanged();
    }
  }

  /**
   * Handler for `focus` event
   *
   * @private
   */
  _handleFocus() {
    if (this.selected) {
      return;
    }

    this.selected = true;
  }

  /**
   * Handler for `click` event
   *
   * @private
   */
  _handleClick() {
    if (this.expanded) {
      this.expanded = false;
    } else {
      this.expanded = true;
    }

    // Call expanded changed in case attributeChangedCallback is not triggered - IE10 issue
    this._expandedChanged();
  }

  /**
   * Called when `selected` attribute is changed
   *
   * @private
   */
  _selectedChanged() {
    if (this.selected) {
      this.setAttribute('aria-selected', 'true');
      this.tabIndex = 0;
      this.$emit('tabselected', this);
    } else {
      this.setAttribute('aria-selected', 'false');
      this.tabIndex = -1;
    }
  }

  /**
   * Called when `expanded` attribute is changed
   *
   * @private
   */
  _expandedChanged() {
    if (this.expanded) {
      this.setAttribute('aria-expanded', 'true');
      this.$emit('tabexpanded', this);
    } else {
      this.setAttribute('aria-expanded', 'false');
      this.$emit('tabcollapsed', this);
    }
  }
}

customElements.define(WPAccordionHeading.is, WPAccordionHeading);
